import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Plus, CalendarDays, Clock, MapPin, UserCheck, Users, Copy,
  ExternalLink, Loader2, Trash2, ListChecks, Pencil, Link2,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import EventWizard from "@/components/EventWizard";
import EventAttendanceModal from "@/components/EventAttendanceModal";
import EventEditModal from "@/components/EventEditModal";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import LinkGlobalEventModal from "@/components/LinkGlobalEventModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
}

export default function EventsTab({ cohortId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [attendanceFor, setAttendanceFor] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cohorts/${cohortId}/events`, { withCredentials: true });
      setEvents(data);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleDelete = async () => {
    const ev = deleteTarget;
    if (!ev) return;
    try {
      if (ev.linked) {
        // Linked global event → unlink only, never destroy the source.
        await axios.delete(
          `${API}/cohorts/${cohortId}/events/link/${ev.id}`,
          { withCredentials: true },
        );
        toast.success("Event unlinked from cohort");
      } else {
        await axios.delete(`${API}/events/${ev.id}`, { withCredentials: true });
        toast.success("Event deleted");
      }
      fetchEvents();
    } catch {
      toast.error(ev?.linked ? "Unlink failed" : "Delete failed");
      throw new Error("delete failed");
    }
  };

  const copyLink = (eventId) => {
    const url = `${window.location.origin}/checkin/${eventId}`;
    navigator.clipboard.writeText(url);
    toast.success("Check-in link copied");
  };

  return (
    <div className="space-y-4" data-testid="events-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-heading font-bold text-white">Events</h3>
          <p className="text-sm font-body text-[#6E7787] mt-0.5">
            Sessions scheduled for this cohort. Each has its own public check-in URL.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() => setWizardOpen(true)}
          data-testid="event-wizard-btn"
          className="h-10 px-4 font-body text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Event Wizard
        </Button>
        <Button
          onClick={() => setLinkOpen(true)}
          data-testid="link-existing-event-btn"
          variant="outline"
          className="h-10 px-4 font-body rounded-lg border-white/15 bg-white/5 text-white hover:bg-white/10"
        >
          <Link2 className="h-4 w-4 mr-1.5" /> Link Existing Event
        </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-[#11141A] border border-white/10 rounded-xl" data-testid="events-empty">
          <CalendarDays className="h-10 w-10 text-[#6E7787] mx-auto mb-3" />
          <p className="text-[#6E7787] font-body mb-1">No events yet.</p>
          <p className="text-xs text-[#6E7787] font-body">Use the Event Wizard to create your first session.</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="events-list">
          {events.map((ev) => {
            const url = `${window.location.origin}/checkin/${ev.id}`;
            return (
              <div
                key={ev.id}
                className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-5 space-y-3"
                data-testid={`event-row-${ev.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <h4 className="font-heading text-base font-semibold text-white break-words flex items-center gap-2">
                      {ev.name}
                      {ev.linked && (
                        <Badge
                          data-testid={`event-linked-badge-${ev.id}`}
                          className="bg-sky-500/15 text-sky-300 border-sky-500/20 text-[10px] uppercase tracking-wider"
                        >
                          <Link2 className="h-2.5 w-2.5 mr-1" /> Linked
                        </Badge>
                      )}
                    </h4>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-body text-[#A1A9B8]">
                      <span className="flex items-center gap-1.5">
                        <CalendarDays className="h-3 w-3" />{formatDate(ev.date)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />{ev.start_time || "—"}{ev.end_time ? ` – ${ev.end_time}` : ""}
                      </span>
                      {ev.location && (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-3 w-3" />{ev.location}
                        </span>
                      )}
                      {ev.instructor && (
                        <span className="flex items-center gap-1.5">
                          <UserCheck className="h-3 w-3" />{ev.instructor}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge
                    className="shrink-0 bg-emerald-500/15 text-emerald-400 border-emerald-500/20 cursor-pointer hover:bg-emerald-500/25"
                    data-testid={`event-attendance-${ev.id}`}
                    onClick={() => setAttendanceFor(ev)}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    {ev.attendance_count} checked in
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
                  <button
                    onClick={() => setAttendanceFor(ev)}
                    className="flex items-center gap-1.5 text-xs font-body text-white px-3 py-1.5 rounded-md transition-colors hover:opacity-90"
                    style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
                    data-testid={`event-attendance-btn-${ev.id}`}
                  >
                    <ListChecks className="h-3.5 w-3.5" /> View Attendance
                  </button>
                  <code className="flex-1 min-w-0 text-[11px] font-mono truncate px-2 py-1 rounded bg-[#0A0C10] text-[#A1A9B8] border border-white/5">
                    {url}
                  </code>
                  <button
                    onClick={() => copyLink(ev.id)}
                    className="p-2 rounded hover:bg-white/5 transition-colors"
                    title="Copy check-in link"
                    data-testid={`event-copy-${ev.id}`}
                  >
                    <Copy className="h-3.5 w-3.5 text-[#A1A9B8]" />
                  </button>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded hover:bg-white/5 transition-colors"
                    title="Open check-in page"
                    data-testid={`event-open-${ev.id}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5 text-[#A1A9B8]" />
                  </a>
                  {!ev.linked && (
                  <button
                    onClick={() => setEditTarget(ev)}
                    className="p-2 rounded hover:bg-white/5 transition-colors"
                    title="Edit event"
                    data-testid={`event-edit-${ev.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5 text-[#A1A9B8]" />
                  </button>
                  )}
                  <button
                    onClick={() => setDeleteTarget(ev)}
                    className="p-2 rounded hover:bg-red-500/10 transition-colors"
                    title={ev.linked ? "Unlink from cohort" : "Delete event"}
                    data-testid={`event-delete-${ev.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <EventWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        cohortId={cohortId}
        onCreated={() => fetchEvents()}
      />

      <EventAttendanceModal
        open={!!attendanceFor}
        onClose={() => { setAttendanceFor(null); fetchEvents(); }}
        event={attendanceFor}
        cohortId={cohortId}
      />

      <EventEditModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        event={editTarget}
        onSaved={() => fetchEvents()}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        tier={1}
        entityName={deleteTarget?.name}
        description={
          deleteTarget?.linked
            ? "Unlink this event from the cohort? The event itself will stay in the Events Database."
            : undefined
        }
        confirmLabel={deleteTarget?.linked ? "Unlink" : undefined}
        onConfirm={handleDelete}
      />

      <LinkGlobalEventModal
        open={linkOpen}
        onOpenChange={setLinkOpen}
        cohortId={cohortId}
        existingLinkedIds={events.filter((e) => e.linked).map((e) => e.id)}
        onLinked={fetchEvents}
      />
    </div>
  );
}
